import './style.css';
import { useMemo, useEffect, useState, useRef, useContext } from 'react';
import { useCalculator } from './hooks/use-calculator';
import { useLoanSubmit } from './hooks/use-loan-submit';
import {
    ApplicationAcceptedOk,
    applicationResponseTypeDict,
    BrandedLoanCalculatorProps,
    FieldValuesType,
    LabelsType,
    StepCommonProps,
    SubmitApplicationOk,
} from './types';
import { getLabel } from './labels';
import { ProgressBar } from '../../progress-bar/progress-bar';
import { SESSION_STORAGE_STEP_KEY, useValues } from './hooks/use-values';
import { Intro } from './components/steps/intro';
import { StepOne } from './components/steps/step-one';
import { StepTwo } from './components/steps/step-two';
import { StepThree } from './components/steps/step-three';
import { ApprovedApplication } from './components/approved-application';
import { RejectedApplication } from './components/rejected-application';
import { ManualApplication } from './components/manual-application';
import { SubmittedApplication } from './components/submitted-application';
import { SubmitErrorApplication } from './components/submit-error-application';

import { mockService, MockSubmitType } from './mock-service';
import { AppContext } from '@/contexts/app-context';
import { ConsumerApplicationProduct, GenericEvent, useDataLayer } from '@/hooks/use-data-layer';

export type CalculatorProps = ReturnType<typeof useCalculator>;
export type CalculatorStateProps = CalculatorProps['calculatorState'];

export type StepIntroProps = StepCommonProps & {
    calculator: CalculatorProps & {
        loanAmountMin: number;
        loanAmountMax: number;
        tenorYearsMin: number;
        tenorYearsMax: number;
    };
    common: {
        labels: LabelsType;
        commonTermsUrl: { url: string };
        secciUrl: { url: string };
        props: BrandedLoanCalculatorProps;
    };
    login: () => void;
    partner?: string;
};

export const BrandedConsumerLoan = (props: BrandedLoanCalculatorProps) => {
    const {
        labels,
        loanAmountMin,
        loanAmountMax,
        loanAmountDefault,
        tenorYearsMin,
        tenorYearsMax,
        tenorYearsDefault,
        commonTermsUrl,
        secciUrl,
        productType,
        insuranceLinks,
        fixedRate,
        partner = 'Carla',
        showTestApplyLinks = false,
        brandColor = '#00807b',
        loanType = 'MOTOR',
    } = props;
    const context = useContext(AppContext);
    const { calculatorState, updateState, monthlyAmount, formatNumber } = useCalculator({
        loanAmountDefault,
        tenorYearsDefault,
        partner,
        productType,
        fixedRate,
    });
    const { dataLayerPush } = useDataLayer();
    const { submit, acceptQuote } = useLoanSubmit();
    const { values, setValues, step, setStep, getValuesFromStorage, resetAll, decisionResult, setDecisionResult } = useValues(loanType);

    const containerRef = useRef<HTMLDivElement>(null);

    const [showError, setShowError] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [dataLayerEvents, setDataLayerEvents] = useState<string[]>([]);

    const currentStep = useMemo(() => (decisionResult ? 4 : step), [decisionResult, step]);
    const shouldShowProgressBar = useMemo(
        () => context?.user?.isAuthenticated && !isSubmitted && !showError && ((step >= 1 && step <= 3) || decisionResult),
        [context, isSubmitted, showError, step, decisionResult]
    );

    const getLabelCallback = (key: keyof LabelsType, replacements?: Record<string, string | number>) => getLabel(labels, key, replacements);

    const stepTitles: string[] = [
        getLabelCallback('formStepOneTitle'),
        getLabelCallback('formStepTwoTitle'),
        getLabelCallback('formStepThreeTitle'),
        getLabelCallback('formStepResultTitle'),
    ];

    const DATALAYER_DEFAULTS = {
        Product: ConsumerApplicationProduct['BRANDED_CONSUMER_LOAN'],
        ProductVariant: partner || 'Carla',
    };

    useEffect(() => {
        const event: GenericEvent = {
            ...DATALAYER_DEFAULTS,
            event: step === 0 ? 'ApplicationStart' : 'ApplicationProgress',
            StepNumber: `${step}`,
            StepName: step === 0 ? 'Calculator' : (stepTitles[step - 1] ?? ''),
        };

        if (!dataLayerEvents.includes(`${step}`)) {
            dataLayerPush(event);
            setDataLayerEvents([...dataLayerEvents, `${step}`]);
        }
    }, [step]);

    const scrollToTop = () => {
        if (containerRef?.current) {
            window.scrollTo({
                behavior: 'smooth',
                top: containerRef.current.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 250,
            });
        }
    };

    useEffect(() => {
        scrollToTop();
    }, [step, decisionResult, isSubmitted, showError]);

    useEffect(() => {
        if (!containerRef?.current) {
            return;
        }

        containerRef.current.querySelectorAll('a').forEach(link => {
            try {
                const url = new URL(link.href);
                if (window?.location?.hostname !== url.hostname) {
                    link.classList.add('external-link');
                }
            } catch {
                // Ignore invalid URLs
            }
        });
    }, [containerRef, step]);

    const handleLogin = () => {
        sessionStorage.setItem(SESSION_STORAGE_STEP_KEY, '1');

        const redirectUrl = `${context.url}${location.pathname}`;
        const scope = 'carlaScope';

        window.location.href = `/login?customRedirectUrl=${redirectUrl}&scope=${scope}`;
    };

    const onSubmit = async (mockType?: MockSubmitType) => {
        try {
            dataLayerPush({
                ...DATALAYER_DEFAULTS,
                event: 'ApplicationSubmitted',
            });

            let response: SubmitApplicationOk;
            if (mockType) {
                response = await mockService.submit(mockType);
            } else {
                response = await submit(values, calculatorState);
            }

            dataLayerPush({
                ...DATALAYER_DEFAULTS,
                event: 'ApplicationStatus',
                ApplicationID: response.result.submitApplicationExtResult?.applicationReference || '',
                Status: applicationResponseTypeDict[response.result.submitApplicationExtResult?.decision] || 'Unknown',
            });

            setDecisionResult(response.result.submitApplicationExtResult);
        } catch {
            setShowError(true);
        }
    };

    const onAcceptQuote = async (mockType?: 'OK') => {
        if (!decisionResult) {
            return;
        }

        try {
            let response: ApplicationAcceptedOk;
            if (mockType) {
                response = await mockService.acceptQuote(mockType);
            } else {
                response = await acceptQuote(decisionResult.applicationReference);
            }

            if (response.result.acceptQuoteResult.accepted) {
                dataLayerPush({
                    ...DATALAYER_DEFAULTS,
                    event: 'ApplicationAccepted',
                    ApplicationID: decisionResult?.applicationReference || '',
                    Status: 'Accepted',
                });

                localStorage.setItem('applicationReference', decisionResult.applicationReference);
                resetAll();
                setIsSubmitted(true);
            }
        } catch {
            setShowError(true);
        }
    };

    const progressLabels = useMemo(() => {
        if (decisionResult) {
            return {
                current: stepTitles[3],
                next: '',
            };
        }
        const current = stepTitles[step - 1] ?? '';
        const next = stepTitles[step] ?? '';
        return {
            current,
            next,
        };
    }, [step, stepTitles, decisionResult]);

    const renderStepContent = () => {
        const commonProps: StepCommonProps = {
            step,
            setStep: setStep,
            setValues: (values: FieldValuesType) => setValues(values),
            fields: values,
            getValuesFromStorage,
            updateFields: (updatedValues: Partial<FieldValuesType>) => setValues(previousValues => ({ ...previousValues, ...updatedValues })),
            getLabel: getLabelCallback,
            showTestApplyLinks,
            brandColor,
            scrollToTop,
        };

        if (isSubmitted) {
            return <SubmittedApplication {...commonProps} />;
        }

        if (decisionResult?.decision === 'APPROVED') {
            return <ApprovedApplication {...commonProps} result={decisionResult} onAccept={onAcceptQuote} />;
        }
        if (decisionResult?.decision === 'REJECTED') {
            return <RejectedApplication {...commonProps} onFinish={resetAll} />;
        }
        if (decisionResult?.decision === 'MANUAL_INSPECTION') {
            return <ManualApplication {...commonProps} onFinish={resetAll} />;
        }

        if (showError || (!context?.user?.isAuthenticated && step > 0)) {
            return <SubmitErrorApplication {...commonProps} />;
        }

        switch (step) {
            case 0:
                return (
                    <Intro
                        calculator={{
                            calculatorState,
                            updateState,
                            monthlyAmount,
                            formatNumber,
                            loanAmountMin,
                            loanAmountMax,
                            tenorYearsMin,
                            tenorYearsMax,
                        }}
                        common={{
                            labels,
                            commonTermsUrl,
                            secciUrl,
                            props,
                        }}
                        login={handleLogin}
                        partner={partner}
                        {...commonProps}
                    />
                );
            case 1:
                return <StepOne {...commonProps} />;
            case 2:
                return <StepTwo {...commonProps} insuranceLinks={insuranceLinks} />;
            case 3:
                return <StepThree {...commonProps} calculator={calculatorState} onSubmit={onSubmit} />;
        }
    };

    return (
        <div data-component="branded-consumer-loan" className="r-container r-pb-8" ref={containerRef}>
            <div className="r-relative r-mx-auto r-max-w-2xl">
                {shouldShowProgressBar && (
                    <div className="r-mb-8">
                        <ProgressBar
                            totalSteps={4}
                            currentStep={currentStep}
                            currentLabel={progressLabels.current}
                            nextLabel={progressLabels.next}
                            brandColor={brandColor}
                        />
                    </div>
                )}
            </div>
            {renderStepContent()}
        </div>
    );
};
